import { FC, useEffect } from "react";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import { toast } from "sonner";
import { useFormik } from "formik";
import useHandleErrors from "../../../hooks/useHandleErrors";
import useCompanies from "../../../hooks/api-calls/useCompanies";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";
import * as yup from "yup";
import { useAtom } from "jotai";
import { formsSubmitAtom } from "../../../atom/atom";
import { GeneralExpenseService } from "../../../services/generalExpense/generalExpenseService";
import { DatePicker } from "../../../components/forms/DatePicker";

interface GeneralExpenseFormProps {    
    onSuccess: () => void;
    expense?: any | null;
}

const schema = yup.object({
    concept: yup.string().required('El concepto es obligatorio'),
    date: yup.date().required('La fecha es obligatoria'),
    amount: yup.number().required('El importe es obligatorio'),
})

const superAdminSchema = schema.concat(yup.object({
    company: yup.string().required('La compañía es obligatoria'),
}))

const GeneralExpenseForm: FC<GeneralExpenseFormProps & { formRef?: any }> = ({ onSuccess, expense, formRef }) => {
    console.log(expense)
    const service = new GeneralExpenseService();
    const isEdit = !!expense;
    
    const [, setIsSubmitting] = useAtom(formsSubmitAtom);
    const { handleErrors } = useHandleErrors();
    const { getCompaniesList } = useCompanies();
    const { userCan } = usePrivilege();

    const formik = useFormik({
        initialValues: {
            id: expense?.id ?? '',
            concept: expense?.concept ?? '',
            date: expense?.date?.date ?? '',
            amount: expense?.amount ?? '',
            company: expense?.company?.id ?? ''
        },
        validationSchema: userCan('list', 'company') ? superAdminSchema : schema,
        validateOnBlur: false,
        onSubmit: values => { 
            if(!isEdit) {
                handleCreate(values)
            } else {
                handleEdit(values)
            }
        },
    });

    const handleCreate = async (values: any) => {
        try {
            const response = await (await service.createGeneralExpense(values)).getResponseData();
            if (response.success) {
                onSuccess();
                toast.success('Gasto creado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    const handleEdit = async (values: any) => {
        try {
            const response = await (await service.editGeneralExpense(values)).getResponseData();   
            if (response.success) {
                onSuccess();
                toast.success('Gasto editado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            handleErrors(error);
        }
    };

    useEffect(() => {
        setIsSubmitting(formik.isSubmitting);
    }, [formik.isSubmitting, setIsSubmitting]);
    
    useEffect(() => {
        if (!formik.values.company && getCompaniesList().length > 0) {
            let defaultCompany = getCompaniesList()?.find((g: any) => g.label == 'GESTION DE RESIDUOS 2023 SLU');
            formik.setFieldValue('company', defaultCompany?.value ?? getCompaniesList()[0].value);
        }
    }, [getCompaniesList]);
    
    // RENDER

    return (
         <div className="@container">
            <form onSubmit={formik.handleSubmit} ref={formRef}>
                <div className={'md:col-span-12'}>
                    <div className='md:grid md:grid-cols-12 gap-4'>
                        <RequiredInput
                            id='concept'
                            label="Concepto"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.concept}  
                        />
                        <div className='md:col-span-12'>
                            <label className="mb-1 block">Fecha</label>
                            <DatePicker
                                selected={formik.values.date}
                                onChange={(date: Date) => formik.setFieldValue('date', date)}
                                placeholderText="Selecciona la fecha"
                            />
                            {formik.errors.date && <div className="text-red-500 text-xs mt-1">{formik.errors.date as string}</div>}
                        </div>

                        <RequiredInput
                            id='amount'
                            label="Importe"
                            type="number"
                            className='md:col-span-12'
                            formik={formik}
                            error={formik.errors.amount}  
                        />

                        {userCan('list', 'company') && (
                            <CustomSelect
                                required
                                isSearchable
                                id={'company'}
                                label="Compañía"
                                containerClassName='md:col-span-12'
                                value={
                                    formik.values.company ?
                                    { value: formik.values.company, label: getCompaniesList()?.find((g: any) => g.value === formik.values.company)?.label } :
                                    null
                                }
                                options={getCompaniesList()}
                                onChange={(e: any) => formik.setFieldValue('company', e?.value)}
                                error={formik.errors.company}
                            />
                        )}                    
                    </div>
                </div>
            </form>
        </div>
    );
};

export default GeneralExpenseForm;
